import { Twitter, Instagram, Youtube, Twitch, Globe, MessageCircle, Music2 } from 'lucide-react';

// Single source of truth for the social platforms we support on profiles.
// Keys persisted in `profiles.social_links` jsonb — never rename without a migration.
export type SocialKey = 'twitter' | 'instagram' | 'tiktok' | 'youtube' | 'twitch' | 'discord' | 'website';

export const SOCIAL_PLATFORMS: ReadonlyArray<{
  key: SocialKey;
  label: string;
  placeholder: string;
  icon: typeof Twitter;
  /** Builds a public URL from a raw handle or full URL. Returns null when the input is unusable. */
  toUrl: (raw: string) => string | null;
  /** Pretty-print the saved value (handle for handles, hostname for URLs, raw text for discord). */
  display: (raw: string) => string;
}> = [
  {
    key: 'twitter',
    label: 'Twitter / X',
    placeholder: '@handle',
    icon: Twitter,
    toUrl: (raw) => {
      const t = raw.trim().replace(/^@/, '');
      if (!t) return null;
      if (/^https?:\/\//i.test(t)) return t;
      return `https://x.com/${encodeURIComponent(t)}`;
    },
    display: (raw) => (raw.startsWith('@') ? raw : `@${raw.replace(/^https?:\/\/(www\.)?(x|twitter)\.com\//i, '').replace(/\/.*$/, '')}`),
  },
  {
    key: 'instagram',
    label: 'Instagram',
    placeholder: '@handle',
    icon: Instagram,
    toUrl: (raw) => {
      const t = raw.trim().replace(/^@/, '');
      if (!t) return null;
      if (/^https?:\/\//i.test(t)) return t;
      return `https://instagram.com/${encodeURIComponent(t)}`;
    },
    display: (raw) => (raw.startsWith('@') ? raw : `@${raw.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/\/.*$/, '')}`),
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    placeholder: '@handle',
    icon: Music2,
    toUrl: (raw) => {
      const t = raw.trim().replace(/^@/, '');
      if (!t) return null;
      if (/^https?:\/\//i.test(t)) return t;
      return `https://tiktok.com/@${encodeURIComponent(t)}`;
    },
    display: (raw) => (raw.startsWith('@') ? raw : `@${raw.replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/i, '').replace(/\/.*$/, '')}`),
  },
  {
    key: 'youtube',
    label: 'YouTube',
    placeholder: '@channel or URL',
    icon: Youtube,
    toUrl: (raw) => {
      const t = raw.trim();
      if (!t) return null;
      if (/^https?:\/\//i.test(t)) return t;
      return `https://youtube.com/${t.startsWith('@') ? t : `@${t}`}`;
    },
    display: (raw) => raw.replace(/^https?:\/\/(www\.)?youtube\.com\//i, ''),
  },
  {
    key: 'twitch',
    label: 'Twitch',
    placeholder: 'username',
    icon: Twitch,
    toUrl: (raw) => {
      const t = raw.trim().replace(/^@/, '');
      if (!t) return null;
      if (/^https?:\/\//i.test(t)) return t;
      return `https://twitch.tv/${encodeURIComponent(t)}`;
    },
    display: (raw) => raw.replace(/^https?:\/\/(www\.)?twitch\.tv\//i, ''),
  },
  {
    key: 'discord',
    // Discord usernames aren't directly linkable, so we display them as text only.
    label: 'Discord',
    placeholder: 'username',
    icon: MessageCircle,
    toUrl: () => null,
    display: (raw) => raw,
  },
  {
    key: 'website',
    label: 'Website',
    placeholder: 'https://example.com',
    icon: Globe,
    toUrl: (raw) => {
      const t = raw.trim();
      if (!t) return null;
      return /^https?:\/\//i.test(t) ? t : `https://${t}`;
    },
    display: (raw) => raw.replace(/^https?:\/\//i, '').replace(/\/$/, ''),
  },
] as const;

export type SocialLinksMap = Partial<Record<SocialKey, string>>;

/** Read-only badges/links for a profile page. Skips empty entries. */
export function SocialLinksDisplay({ links, className }: { links: SocialLinksMap | null | undefined; className?: string }) {
  const entries = (Object.entries(links || {}) as [SocialKey, string | undefined][])
    .filter(([, v]) => typeof v === 'string' && v.trim().length > 0);
  if (entries.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className ?? ''}`}>
      {entries.map(([key, value]) => {
        const p = SOCIAL_PLATFORMS.find((x) => x.key === key);
        if (!p) return null;
        const url = p.toUrl(value as string);
        const Icon = p.icon;
        const label = p.display(value as string);
        const inner = (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-[11px] text-foreground/90 font-body border border-border hover:border-primary/60 transition-colors">
            <Icon className="h-3 w-3 text-primary" aria-hidden />
            <span className="truncate max-w-[160px]">{label}</span>
          </span>
        );
        return url ? (
          <a
            key={key}
            href={url}
            target="_blank"
            rel="me noopener noreferrer"
            aria-label={`${p.label}: ${label}`}
          >
            {inner}
          </a>
        ) : (
          <span key={key} aria-label={`${p.label}: ${label}`}>{inner}</span>
        );
      })}
    </div>
  );
}
